var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { create } from "zustand";
import { sendAjaxRequest } from "../API";
const LOGPREFIX = "ChatStore: ";
export const useChatStore = create((set, get) => ({
    messages: [],
    loading: false,
    loadingMore: false,
    error: undefined,
    polling: false,
    sessionId: "",
    lastMessageId: undefined,
    hasMoreMessages: true,
    setSessionId: (sessionId) => {
        set({
            sessionId,
            messages: [],
            error: undefined,
            lastMessageId: undefined,
            hasMoreMessages: true,
        });
    },
    setPolling: (polling) => {
        set({ polling });
    },
    clearMessages: () => {
        set({
            messages: [],
            error: undefined,
            lastMessageId: undefined,
            hasMoreMessages: true,
        });
    },
    setError: (error) => {
        set({ error });
    },
    sendMessage: (messageText, sessionId, auth, cacheBusterStore, logger) => __awaiter(void 0, void 0, void 0, function* () {
        logger.log(LOGPREFIX + "sendMessage: " + messageText);
        set({ loading: true, error: undefined });
        try {
            if (!auth.sequence) {
                throw new Error("Missing sequence number in auth");
            }
            const input = {
                operation: "sendMessage",
                sessionId: sessionId,
                messageText: messageText,
                botId: "default",
                bearer: auth.bearer || "",
                sequence: auth.sequence,
            };
            const response = yield new Promise((resolve, reject) => {
                sendAjaxRequest((ok, status, message) => {
                    logger.log(LOGPREFIX +
                        "sendMessage response: ok=" +
                        ok +
                        ", status=" +
                        status +
                        ", message=" +
                        message);
                    if (!ok || status < 200 || status > 299) {
                        logger.error(LOGPREFIX +
                            "sendMessage error: " +
                            message +
                            " (" +
                            status +
                            ")");
                        reject(new Error(message || `HTTP ${status}`));
                        return;
                    }
                    try {
                        const result = JSON.parse(message);
                        resolve(result);
                    }
                    catch (err) {
                        logger.error(LOGPREFIX + "sendMessage parse error: " + err);
                        reject(new Error("Failed to parse response"));
                    }
                }, "chatbot", input, auth.bearer, cacheBusterStore, logger);
            });
            // Update bearer and sequence if provided
            if (response.bearer && response.sequence !== undefined) {
                auth.setBearerAndSequence(response.bearer, response.sequence, logger);
            }
            // Add the user's message optimistically so it appears immediately
            const userMessage = {
                messageId: response.messageId || `local-${Date.now()}`,
                sessionId: sessionId,
                timestamp: Date.now(),
                botId: "default",
                messageText: messageText,
                source: "user",
                context: [],
            };
            const currentMessages = get().messages;
            logger.log(LOGPREFIX + "Message sent successfully");
            set({ messages: [...currentMessages, userMessage], loading: false });
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error";
            logger.error(LOGPREFIX + "Error sending message: " + errorMessage);
            set({ loading: false, error: errorMessage });
            throw error;
        }
    }),
    getMessages: (sessionId, auth, cacheBusterStore, logger) => __awaiter(void 0, void 0, void 0, function* () {
        logger.log(LOGPREFIX + "getMessages for session: " + sessionId);
        try {
            if (!auth.sequence) {
                throw new Error("Missing sequence number in auth");
            }
            const input = {
                operation: "getMessages",
                sessionId: sessionId,
                bearer: auth.bearer || "",
                sequence: auth.sequence,
            };
            const response = yield new Promise((resolve, reject) => {
                sendAjaxRequest((ok, status, message) => {
                    logger.log(LOGPREFIX +
                        "getMessages response: ok=" +
                        ok +
                        ", status=" +
                        status +
                        ", message=" +
                        message);
                    if (!ok || status < 200 || status > 299) {
                        logger.error(LOGPREFIX +
                            "getMessages error: " +
                            message +
                            " (" +
                            status +
                            ")");
                        reject(new Error(message || `HTTP ${status}`));
                        return;
                    }
                    try {
                        const result = JSON.parse(message);
                        resolve(result);
                    }
                    catch (err) {
                        logger.error(LOGPREFIX + "getMessages parse error: " + err);
                        reject(new Error("Failed to parse response"));
                    }
                }, "chatbot", input, auth.bearer, cacheBusterStore, logger);
            });
            // Update bearer and sequence if provided
            if (response.bearer && response.sequence !== undefined) {
                auth.setBearerAndSequence(response.bearer, response.sequence, logger);
            }
            const messages = response.messages || [];
            // Sort messages by timestamp
            messages.sort((a, b) => a.timestamp - b.timestamp);
            // Update pagination state
            const hasMoreMessages = messages.length === 20; // If we got a full page, there might be more
            const lastMessageId = messages.length > 0 ? messages[0].messageId : undefined; // Oldest message for pagination
            if (messages.length > 0) {
                // Server returned messages — use them as the authoritative source
                set({
                    messages,
                    error: undefined,
                    lastMessageId,
                    hasMoreMessages,
                });
            }
            else {
                // Server returned empty — preserve any existing optimistic messages
                set({ error: undefined });
            }
            logger.log(LOGPREFIX + "Loaded " + messages.length + " messages");
            return messages;
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error";
            logger.error(LOGPREFIX + "Error getting messages: " + errorMessage);
            set({ error: errorMessage });
            throw error;
        }
    }),
    loadMoreMessages: (sessionId, auth, cacheBusterStore, logger) => __awaiter(void 0, void 0, void 0, function* () {
        const { lastMessageId, hasMoreMessages, loadingMore } = get();
        if (!hasMoreMessages || loadingMore) {
            logger.log(LOGPREFIX + "No more messages to load or already loading");
            return [];
        }
        logger.log(LOGPREFIX +
            "loadMoreMessages for session: " +
            sessionId +
            ", lastMessageId: " +
            lastMessageId);
        set({ loadingMore: true, error: undefined });
        try {
            if (!auth.sequence) {
                throw new Error("Missing sequence number in auth");
            }
            const input = {
                operation: "getMessages",
                sessionId: sessionId,
                lastMessageId: lastMessageId,
                bearer: auth.bearer || "",
                sequence: auth.sequence,
            };
            const response = yield new Promise((resolve, reject) => {
                sendAjaxRequest((ok, status, message) => {
                    logger.log(LOGPREFIX +
                        "loadMoreMessages response: ok=" +
                        ok +
                        ", status=" +
                        status +
                        ", message=" +
                        message);
                    if (!ok || status < 200 || status > 299) {
                        logger.error(LOGPREFIX +
                            "loadMoreMessages error: " +
                            message +
                            " (" +
                            status +
                            ")");
                        reject(new Error(message || `HTTP ${status}`));
                        return;
                    }
                    try {
                        const result = JSON.parse(message);
                        resolve(result);
                    }
                    catch (err) {
                        logger.error(LOGPREFIX + "loadMoreMessages parse error: " + err);
                        reject(new Error("Failed to parse response"));
                    }
                }, "chatbot", input, auth.bearer, cacheBusterStore, logger);
            });
            // Update bearer and sequence if provided
            if (response.bearer && response.sequence !== undefined) {
                auth.setBearerAndSequence(response.bearer, response.sequence, logger);
            }
            const newMessages = response.messages || [];
            // Sort new messages by timestamp
            newMessages.sort((a, b) => a.timestamp - b.timestamp);
            // Merge with existing messages (prepend older messages)
            const currentMessages = get().messages;
            const mergedMessages = [...newMessages, ...currentMessages];
            // Update pagination state
            const hasMoreMessages = newMessages.length === 20; // If we got a full page, there might be more
            const newLastMessageId = newMessages.length > 0
                ? newMessages[0].messageId
                : get().lastMessageId;
            set({
                messages: mergedMessages,
                error: undefined,
                loadingMore: false,
                lastMessageId: newLastMessageId,
                hasMoreMessages,
            });
            logger.log(LOGPREFIX +
                "Loaded " +
                newMessages.length +
                " more messages, total: " +
                mergedMessages.length);
            return newMessages;
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error";
            logger.error(LOGPREFIX + "Error loading more messages: " + errorMessage);
            set({ error: errorMessage, loadingMore: false });
            throw error;
        }
    }),
}));
